import { AvatarSpecSchema } from "@uzverse/contracts";
import { useSyncExternalStore } from "react";
import { defaultUserSpec, mhOf, normalizeSpec, withMh } from "./spec.js";
import type { AvatarSpec } from "./spec.js";

/**
 * The user's own character. It lives in the user settings on the gateway (`avatar`, a spec over the
 * bundled models); localStorage is the instant cache the first paint reads. Without a saved choice
 * the character is a deterministic MakeHuman preset for this user's name (`defaultUserSpec`), so
 * the avatar is never a placeholder. Specs saved before styles existed are migrated to their
 * nearest MakeHuman preset on read (`asMh`) and saved back with the next save; an explicit office
 * or classic choice is kept.
 */
export interface MyAvatar {
  spec: AvatarSpec;
  /** True once the user saved a choice. */
  chosen: boolean;
  /** Shown above the character in third person. */
  name: string;
}

export interface AvatarTransport {
  get(): Promise<AvatarSpec | null>;
  put(spec: AvatarSpec): Promise<void>;
}

const SETTINGS_URL = "/api/v1/settings";
const CACHE_KEY = "uzverse.avatar";

export const httpAvatar: AvatarTransport = {
  async get() {
    const res = await fetch(SETTINGS_URL, { credentials: "same-origin" });
    if (!res.ok) throw new Error(`settings: ${res.status}`);
    const body = (await res.json()) as { settings?: { avatar?: unknown } };
    const parsed = AvatarSpecSchema.safeParse(body.settings?.avatar);
    return parsed.success ? parsed.data : null;
  },
  async put(spec) {
    const res = await fetch(SETTINGS_URL, {
      method: "PATCH",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ avatar: spec }),
    });
    if (!res.ok) throw new Error(`settings: ${res.status}`);
  },
};

/** The in-browser fake gateway keeps the spec in memory (and on `window.__uzMockAvatar`). */
export function createMemoryAvatar(initial: AvatarSpec | null = null): AvatarTransport {
  let data = initial;
  return {
    get: () => Promise.resolve(data),
    put: (spec) => {
      data = spec;
      (globalThis as { __uzMockAvatar?: AvatarSpec }).__uzMockAvatar = spec;
      return Promise.resolve();
    },
  };
}

function defaultTransport(): AvatarTransport | null {
  if (import.meta.env.MODE === "test") return null;
  if (import.meta.env["VITE_UZ_MOCK"] === "1") return createMemoryAvatar();
  return httpAvatar;
}
let transport: AvatarTransport | null = defaultTransport();
export function setAvatarTransport(next: AvatarTransport | null): void {
  transport = next;
}

/**
 * A saved spec as it is shown: MakeHuman ones as they are, an explicit `office` or `classic` choice
 * kept (those stay options), a spec saved before styles existed migrated to its nearest
 * MakeHuman preset.
 */
export function asMh(spec: AvatarSpec): AvatarSpec {
  if (spec.style === "mh" && spec.mh) return normalizeSpec(spec);
  if (spec.style === "office" || spec.style === "classic") return normalizeSpec(spec);
  return withMh(mhOf(spec));
}

function readCache(): AvatarSpec | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const parsed = raw ? AvatarSpecSchema.safeParse(JSON.parse(raw)) : null;
    return parsed?.success ? asMh(parsed.data) : null;
  } catch {
    return null;
  }
}
function writeCache(spec: AvatarSpec): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(spec));
  } catch {
    // Private mode: the server copy still holds it.
  }
}

let seed = "me";
let name = "";
const cached = readCache();
let state: MyAvatar = { spec: cached ?? defaultUserSpec(seed), chosen: cached !== null, name };
const listeners = new Set<() => void>();
const emit = (next: MyAvatar) => {
  state = next;
  for (const l of listeners) l();
};

export const getMyAvatar = (): MyAvatar => state;
export function subscribeMyAvatar(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}
export function useMyAvatar(): MyAvatar {
  return useSyncExternalStore(subscribeMyAvatar, getMyAvatar, getMyAvatar);
}

/** Who the signed-in user is: the default look is derived from the id, the label from the name. */
export function identifyMe(userId: string, displayName: string): void {
  seed = userId;
  name = displayName;
  emit({ ...state, spec: state.chosen ? state.spec : defaultUserSpec(seed, name), name });
}

/** Reads the saved character (the server wins over this device's cache). */
export async function syncMyAvatar(): Promise<void> {
  if (!transport) return;
  try {
    const remote = await transport.get();
    if (remote) {
      writeCache(remote);
      emit({ ...state, spec: asMh(remote), chosen: true });
    } else if (state.chosen) {
      void transport.put(state.spec).catch(() => undefined);
    }
  } catch {
    // Keep the cache.
  }
}

/** Saves a choice: the cache and the store update at once, the server follows. */
export async function saveMyAvatar(spec: AvatarSpec): Promise<void> {
  const next = normalizeSpec(spec);
  writeCache(next);
  emit({ ...state, spec: next, chosen: true });
  if (transport) await transport.put(next);
}

/** Test helper: forget everything. */
export function resetMyAvatar(): void {
  seed = "me";
  name = "";
  emit({ spec: defaultUserSpec(seed), chosen: false, name });
}
